import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";

const STORAGE_KEY = "nexus-cookie-consent";

const categories = [
  {
    key: "necessary",
    label: "Strictly Necessary",
    desc: "Required for authentication and session management. Cannot be disabled.",
    locked: true,
  },
  {
    key: "analytics",
    label: "Analytics",
    desc: "Helps us understand how features are used to improve the platform. No personal data shared with third parties.",
    locked: false,
  },
  {
    key: "marketing",
    label: "Marketing",
    desc: "For relevant product communications and retargeting. Only enabled with your explicit consent.",
    locked: false,
  },
];

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={`relative w-9 h-5 rounded-full transition-colors duration-300 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF4F00] focus-visible:ring-offset-2 focus-visible:ring-offset-[#060C0C] ${
        checked ? "bg-[#FF4F00]" : "bg-[#152222]"
      }`}
    >
      <span
        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-300 ${
          checked ? "translate-x-[18px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        const t = setTimeout(() => setVisible(true), 900);
        return () => clearTimeout(t);
      }
    } catch {}
  }, []);

  useEffect(() => {
    const handleReopen = () => {
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
      setPrefs({ necessary: true, analytics: false, marketing: false });
      setShowSettings(false);
      setVisible(true);
    };
    window.addEventListener("nexus:reopen-cookie-consent", handleReopen);
    return () => window.removeEventListener("nexus:reopen-cookie-consent", handleReopen);
  }, []);

  const save = (p) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...p, decided: true }));
    } catch {}
    setVisible(false);
  };

  const toggle = (key) => setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-6 left-4 right-4 sm:left-6 sm:right-auto sm:w-[340px] z-[60]"
          data-testid="cookie-consent"
          role="dialog"
          aria-label="Cookie preferences"
          aria-modal="false"
        >
          <div className="glass rounded-2xl border border-[#152222] shadow-[0_20px_60px_rgba(0,0,0,0.7)] overflow-hidden">
            <AnimatePresence mode="wait" initial={false}>
              {!showSettings ? (
                <motion.div
                  key="banner"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="p-5"
                >
                  <p className="text-xs text-[#9BB8B8] leading-relaxed mb-4">
                    <span className="font-semibold text-white block mb-1">
                      Cookie preferences
                    </span>
                    We use essential cookies to keep the platform running. With your consent,
                    we also use analytics cookies.{" "}
                    <Link
                      to="/privacy"
                      className="text-white underline underline-offset-2 hover:text-[#FF4F00] transition-colors"
                    >
                      Privacy policy
                    </Link>
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        save({ necessary: true, analytics: false, marketing: false })
                      }
                      className="flex-1 py-2.5 rounded-full border border-[#1F3535] text-[#9BB8B8] text-xs font-medium hover:border-[#FF4F00]/40 hover:text-white transition-all duration-300"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="flex-1 py-2.5 rounded-full border border-[#1F3535] text-[#9BB8B8] text-xs font-medium hover:border-[#FF4F00]/40 hover:text-white transition-all duration-300"
                    >
                      Settings
                    </button>
                    <button
                      onClick={() =>
                        save({ necessary: true, analytics: true, marketing: true })
                      }
                      className="flex-1 group inline-flex items-center justify-center gap-1 py-2.5 rounded-full bg-white text-black text-xs font-semibold hover:bg-[#FF4F00] hover:text-white transition-all duration-300"
                    >
                      Allow
                      <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="settings"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="p-5"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-semibold text-white">Cookie settings</span>
                    <button
                      onClick={() => setShowSettings(false)}
                      aria-label="Back to summary"
                      className="w-7 h-7 rounded-full border border-[#1F3535] text-[#9BB8B8] hover:text-white hover:border-[#FF4F00]/40 transition-all grid place-items-center"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <div className="space-y-2.5 mb-4">
                    {categories.map((c) => (
                      <div
                        key={c.key}
                        className="flex items-start gap-3 p-3 rounded-xl border border-[#0E1A1A] bg-[#060C0C]/40"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-white mb-0.5">
                            {c.label}
                          </div>
                          <div className="text-[11px] text-[#9BB8B8] leading-relaxed">
                            {c.desc}
                          </div>
                        </div>
                        <div className="mt-0.5 shrink-0">
                          {c.locked ? (
                            <span className="text-[10px] font-mono text-[#34D399] whitespace-nowrap">
                              Always on
                            </span>
                          ) : (
                            <Toggle
                              checked={prefs[c.key]}
                              onChange={() => toggle(c.key)}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        save({ necessary: true, analytics: false, marketing: false })
                      }
                      className="flex-1 py-2.5 rounded-full border border-[#1F3535] text-[#9BB8B8] text-xs font-medium hover:border-[#FF4F00]/40 hover:text-white transition-all duration-300"
                    >
                      Reject all
                    </button>
                    <button
                      onClick={() => save(prefs)}
                      className="flex-1 py-2.5 rounded-full bg-white text-black text-xs font-semibold hover:bg-[#FF4F00] hover:text-white transition-all duration-300"
                    >
                      Save preferences
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
